import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './BetaPending.css';
import { apiClient, getApiEndpoint } from './config';

const POLL_INTERVAL_MS = 15000;

function BetaPending() {
    const navigate = useNavigate();
    const [betaRequestDate, setBetaRequestDate] = useState(null);
    const [email, setEmail] = useState('');
    const [ready, setReady] = useState(false); // don't render until guard resolves

    // Guard: verify session against DB; never trust localStorage user data
    useEffect(() => {
        const token = localStorage.getItem('auth_token');
        if (!token) {
            navigate('/login', { replace: true });
            return;
        }

        fetch(getApiEndpoint('/api/auth/verify'), {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => res.json())
            .then(data => {
                if (!data.success || !data.user) {
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('user');
                    navigate('/login', { replace: true });
                    return;
                }

                const user = data.user;

                // Keep localStorage in sync as a cache
                localStorage.setItem('user', JSON.stringify(user));

                if (user.beta_access_approved) {
                    navigate('/', { replace: true });
                    return;
                }

                // If their request was cleared (e.g. rejected), send them to re-request
                if (!user.beta_access_requested) {
                    navigate('/beta-request', { replace: true });
                    return;
                }

                if (user.email) setEmail(user.email);
                if (user.beta_request_date) setBetaRequestDate(user.beta_request_date);
                setReady(true);
            })
            .catch(() => {
                // Network error — best effort fallback
                navigate('/login', { replace: true });
            });
    }, [navigate]);

    const handleApproved = useCallback(() => {
        // Update the localStorage cache so the rest of the app sees the new state
        try {
            const raw = localStorage.getItem('user');
            if (raw) {
                const user = JSON.parse(raw);
                user.beta_access_approved = true;
                localStorage.setItem('user', JSON.stringify(user));
            }
        } catch (e) { /* ignore */ }
        navigate('/');
    }, [navigate]);

    const checkStatus = useCallback(async () => {
        try {
            const { data } = await apiClient.get('/api/beta/status');
            if (data.beta_access_approved) {
                handleApproved();
                return true;
            }
            // Request was rejected (cleared) while the user was on this page
            if (!data.beta_access_requested) {
                navigate('/beta-request', { replace: true });
                return false;
            }
            if (data.beta_request_date) setBetaRequestDate(data.beta_request_date);
        } catch (err) {
            console.error('Status check failed:', err);
        }
        return false;
    }, [handleApproved, navigate]);

    // Auto-poll every 15 s — only after initial guard passes
    useEffect(() => {
        if (!ready) return;
        checkStatus();
        const interval = setInterval(async () => {
            const approved = await checkStatus();
            if (approved) clearInterval(interval);
        }, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [ready, checkStatus]);

    if (!ready) return null;

    return (
        <div className="beta-pending-page">
            <div className="beta-pending-card">
                <span className="pending-icon">⏳</span>

                <h1>Beta Access Pending</h1>

                <p className="pending-message">
                    Your request is being reviewed by our team.
                </p>
                <p className="pending-submessage">
                    You'll receive an email at <strong>{email}</strong> once approved —
                    usually 1–2 business days.
                </p>

                {betaRequestDate && (
                    <div className="pending-info">
                        <p>
                            <strong>Requested on:</strong>{' '}
                            {new Date(betaRequestDate).toLocaleDateString()}
                        </p>
                    </div>
                )}

                <p className="pending-poll-note">
                    Checking for approval automatically every 15 s…
                </p>

                <div className="pending-actions">
                    <button onClick={checkStatus} className="check-status-btn">
                        Check Now
                    </button>
                    <button onClick={() => navigate('/')} className="back-home-btn">
                        Back to Home
                    </button>
                </div>
            </div>
        </div>
    );
}

export default BetaPending;
